// src/vatandas/redux/slices/vatandasApplicationSlice.js

import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import api, {
  fetchPublicOtpConfigAPI,
  preSubmitOtpAPI,
  resendOtpAPI,
  submitVerifiedAPI,
} from "../../services/vatandasApi";

/* =============================================================================
   Helpers
============================================================================= */

function normalizeErrorPayload(payload) {
  if (!payload) return "Bir hata oluştu.";

  if (typeof payload === "string") return payload;

  // Axios error.response.data bazen { detail: "..."} veya alan bazlı hatalar döner
  if (typeof payload === "object") {
    if (payload.detail) return String(payload.detail);

    // DRF field errors: { field: ["msg1", "msg2"] }
    const firstKey = Object.keys(payload)[0];
    if (firstKey) {
      const v = payload[firstKey];
      if (Array.isArray(v) && v.length) return String(v[0]);
      return String(v);
    }

    try {
      return JSON.stringify(payload);
    } catch (_) {
      return "Bir hata oluştu.";
    }
  }

  return "Bir hata oluştu.";
}

/* =============================================================================
   Thunks
============================================================================= */

export const createApplication = createAsyncThunk(
  "vatandasApplication/create",
  async (applicationData, { rejectWithValue }) => {
    try {
      // Axios FormData gönderirken Content-Type'ı otomatik set eder.
      const response = await api.post("/core/applications/", applicationData);
      return response.data;
    } catch (error) {
      const status = error?.response?.status;
      const data = error?.response?.data;

      // 413 (Payload Too Large)
      if (status === 413) {
        return rejectWithValue("Dosya boyutu sınırı aşıldı. Lütfen daha küçük dosya yükleyin.");
      }

      // Backend mesajı varsa onu kullan
      if (data) return rejectWithValue(data);

      // Ağ/diğer
      return rejectWithValue(error?.message || "İstek başarısız.");
    }
  },
  {
    // loading iken yeni create başlatma (çift tıklama vb.)
    condition: (_, { getState }) => {
      const st = getState()?.vatandasApplication;
      return st?.status !== "loading";
    },
  }
);

/**
 * Vatandaş tarafında OTP özelliği açık mı?
 * GET /core/applications/public-config/
 */
export const fetchOtpConfig = createAsyncThunk(
  "vatandasApplication/fetchOtpConfig",
  async (_, { rejectWithValue }) => {
    try {
      const response = await fetchPublicOtpConfigAPI();
      return response.data;
    } catch (error) {
      const data = error?.response?.data;
      if (data) return rejectWithValue(data);
      return rejectWithValue(error?.message || "OTP yapılandırması alınamadı.");
    }
  }
);

/**
 * OTP akışının ilk adımı:
 * Form verisini gönderir, session açar, SMS kodunu başlatır.
 */
export const initOtpSession = createAsyncThunk(
  "vatandasApplication/initOtpSession",
  async (applicationData, { rejectWithValue }) => {
    try {
      const response = await preSubmitOtpAPI(applicationData);
      return response.data;
    } catch (error) {
      const status = error?.response?.status;
      const data = error?.response?.data;

      if (status === 413) {
        return rejectWithValue("Dosya boyutu sınırı aşıldı. Lütfen daha küçük dosya yükleyin.");
      }

      if (data) return rejectWithValue(data);
      return rejectWithValue(error?.message || "Doğrulama oturumu başlatılamadı.");
    }
  },
  {
    condition: (_, { getState }) => {
      const st = getState()?.vatandasApplication;
      return st?.otpStatus !== "loading";
    },
  }
);

/**
 * Aynı session için yeni OTP kodu ister.
 */
export const resendOtp = createAsyncThunk(
  "vatandasApplication/resendOtp",
  async (sessionToken, { rejectWithValue }) => {
    try {
      const response = await resendOtpAPI(sessionToken);
      return response.data;
    } catch (error) {
      const data = error?.response?.data;
      if (data) return rejectWithValue(data);
      return rejectWithValue(error?.message || "Doğrulama kodu tekrar gönderilemedi.");
    }
  },
  {
    condition: (_, { getState }) => {
      const st = getState()?.vatandasApplication;
      return st?.otpVerifyStatus !== "loading";
    },
  }
);

/**
 * OTP kodunu doğrular ve gerçek başvuruyu finalize eder.
 */
export const verifyOtpAndSubmit = createAsyncThunk(
  "vatandasApplication/verifyOtpAndSubmit",
  async ({ sessionToken, code }, { rejectWithValue }) => {
    try {
      const response = await submitVerifiedAPI(sessionToken, code);
      return response.data;
    } catch (error) {
      const data = error?.response?.data;
      if (data) return rejectWithValue(data);
      return rejectWithValue(error?.message || "Kod doğrulanamadı.");
    }
  },
  {
    condition: (_, { getState }) => {
      const st = getState()?.vatandasApplication;
      return st?.otpVerifyStatus !== "loading";
    },
  }
);

/* =============================================================================
   Slice
============================================================================= */

const initialOtpState = {
  otpEnabled: false,           // backend public-config
  otpConfigStatus: "idle",     // idle | loading | succeeded | failed
  otpConfigError: null,

  otpSessionToken: null,
  otpSessionState: null,       // backend session status (code_sent, sms_failed, vs.)
  otpExpiresAt: null,
  otpResendAvailableAt: null,
  otpSmsRemaining: 0,
  otpSmsFailed: false,

  otpStatus: "idle",           // session başlatma statusu
  otpError: null,

  otpVerifyStatus: "idle",     // verify/finalize statusu
  otpVerifyError: null,
};

const vatandasApplicationSlice = createSlice({
  name: "vatandasApplication",
  initialState: {
    lastCreated: null,        // son oluşturulan başvuru
    trackingNumber: null,     // takip numarası
    status: "idle",           // idle | loading | succeeded | failed
    error: null,              // normalize edilmiş string

    ...initialOtpState,
  },
  reducers: {
    resetCreateState(state) {
      state.lastCreated = null;
      state.trackingNumber = null;
      state.status = "idle";
      state.error = null;
    },
    clearError(state) {
      state.error = null;
    },

    // OTP state temizliği (modal kapanışı / sayfadan çıkış vb.)
    resetOtpState(state) {
      state.otpSessionToken = null;
      state.otpSessionState = null;
      state.otpExpiresAt = null;
      state.otpResendAvailableAt = null;
      state.otpSmsRemaining = 0;
      state.otpSmsFailed = false;
      state.otpStatus = "idle";
      state.otpError = null;
      state.otpVerifyStatus = "idle";
      state.otpVerifyError = null;
    },

    clearOtpError(state) {
      state.otpError = null;
      state.otpVerifyError = null;
      state.otpConfigError = null;
    },

    // Gerekirse UI tarafı local close için
    setOtpEnabled(state, action) {
      state.otpEnabled = Boolean(action.payload);
    },
  },
  extraReducers: (builder) => {
    builder
      // ---------------------------------------------------------------------
      // Mevcut create akışı
      // ---------------------------------------------------------------------
      .addCase(createApplication.pending, (state) => {
        state.status = "loading";
        state.error = null;
      })
      .addCase(createApplication.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.lastCreated = action.payload || null;
        state.trackingNumber = action.payload?.tracking_number ?? null;
      })
      .addCase(createApplication.rejected, (state, action) => {
        state.status = "failed";
        state.error = normalizeErrorPayload(action.payload || action.error?.message);
      })

      // ---------------------------------------------------------------------
      // OTP config
      // ---------------------------------------------------------------------
      .addCase(fetchOtpConfig.pending, (state) => {
        state.otpConfigStatus = "loading";
        state.otpConfigError = null;
      })
      .addCase(fetchOtpConfig.fulfilled, (state, action) => {
        state.otpConfigStatus = "succeeded";
        // Backend /api/core/applications/public-config/ şunu döner:
        //   { "otp_application_creation_enabled": true/false }
        // (core_app/views.py — public_config action, field adı buradan gelir.)
        //
        // Geriye dönük uyum için eski alan adı (sms_basvuru_dogrulama) da
        // fallback olarak okunur — eski deploy'larla da çalışsın.
        const payload = action.payload || {};
        state.otpEnabled = Boolean(
          payload.otp_application_creation_enabled ??
          payload.sms_basvuru_dogrulama
        );
      })
      .addCase(fetchOtpConfig.rejected, (state, action) => {
        state.otpConfigStatus = "failed";
        state.otpConfigError = normalizeErrorPayload(action.payload || action.error?.message);
      })

      // ---------------------------------------------------------------------
      // OTP session başlat
      // ---------------------------------------------------------------------
      .addCase(initOtpSession.pending, (state) => {
        state.otpStatus = "loading";
        state.otpError = null;
        state.otpVerifyError = null;
      })
      .addCase(initOtpSession.fulfilled, (state, action) => {
        state.otpStatus = "succeeded";
        state.otpError = null;

        state.otpSessionToken = action.payload?.session_token ?? null;
        state.otpSessionState = action.payload?.status ?? null;
        state.otpExpiresAt = action.payload?.expires_at ?? null;
        state.otpResendAvailableAt = action.payload?.resend_available_at ?? null;
        state.otpSmsRemaining = Number(action.payload?.sms_remaining ?? 0);
        state.otpSmsFailed = Boolean(action.payload?.sms_failed);
      })
      .addCase(initOtpSession.rejected, (state, action) => {
        state.otpStatus = "failed";
        state.otpError = normalizeErrorPayload(action.payload || action.error?.message);
      })

      // ---------------------------------------------------------------------
      // OTP resend
      // ---------------------------------------------------------------------
      .addCase(resendOtp.pending, (state) => {
        state.otpVerifyStatus = "loading";
        state.otpVerifyError = null;
      })
      .addCase(resendOtp.fulfilled, (state, action) => {
        state.otpVerifyStatus = "succeeded";
        state.otpVerifyError = null;

        state.otpSessionState = action.payload?.status ?? state.otpSessionState;
        state.otpExpiresAt = action.payload?.expires_at ?? state.otpExpiresAt;
        state.otpResendAvailableAt = action.payload?.resend_available_at ?? null;
        state.otpSmsRemaining = Number(action.payload?.sms_remaining ?? state.otpSmsRemaining);
        state.otpSmsFailed = Boolean(action.payload?.sms_failed);
      })
      .addCase(resendOtp.rejected, (state, action) => {
        state.otpVerifyStatus = "failed";
        state.otpVerifyError = normalizeErrorPayload(action.payload || action.error?.message);
      })

      // ---------------------------------------------------------------------
      // OTP verify + finalize
      // ---------------------------------------------------------------------
      .addCase(verifyOtpAndSubmit.pending, (state) => {
        state.otpVerifyStatus = "loading";
        state.otpVerifyError = null;
      })
      .addCase(verifyOtpAndSubmit.fulfilled, (state, action) => {
        state.otpVerifyStatus = "succeeded";
        state.otpVerifyError = null;

        // Başvuru başarıyla oluştuysa mevcut success state de güncellensin
        state.status = "succeeded";
        state.lastCreated = action.payload || null;
        state.trackingNumber = action.payload?.tracking_number ?? null;
      })
      .addCase(verifyOtpAndSubmit.rejected, (state, action) => {
        state.otpVerifyStatus = "failed";
        state.otpVerifyError = normalizeErrorPayload(action.payload || action.error?.message);
      });
  },
});

export const {
  resetCreateState,
  clearError,
  resetOtpState,
  clearOtpError,
  setOtpEnabled,
} = vatandasApplicationSlice.actions;

export default vatandasApplicationSlice.reducer;